import React, { useEffect, useRef } from "react";
import type { IHeader, IHeaderCellProps } from "./FilteredTable.types";
import {
  getGlobalRowNumber,
  SYNTHETIC_COLUMN_IDS,
} from "./FilteredTable.utils";
import { useFilteredTableSelection } from "./FilteredTableSelectionContext";

export function IndexColumnCell(props: IHeaderCellProps) {
  return (
    <p className="tabular-nums">
      {getGlobalRowNumber(props.rowIndex, props.pagination)}
    </p>
  );
}

export function SelectionRowCell(props: IHeaderCellProps) {
  const ctx = useFilteredTableSelection();
  if (!ctx) {
    return null;
  }
  const id = ctx.getRowId(props.row);
  const selectable = ctx.isRowSelectable ? ctx.isRowSelectable(props.row) : true;
  const checked = selectable && ctx.selectedIds.has(id);
  if (!selectable) {
    return (
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 cursor-not-allowed rounded border-gray-300 opacity-40"
        checked={false}
        disabled
        aria-label="Row not eligible for selection"
        title="Only member firm recipients with unpaid or partial status can be selected"
      />
    );
  }
  return (
    <input
      type="checkbox"
      className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray accent-Default "
      checked={checked}
      onChange={() => ctx.onToggleRow(id, !checked)}
      aria-label={`Seleccionar fila ${id}`}
    />
  );
}

export function SelectAllHeaderControl() {
  const ctx = useFilteredTableSelection();
  const inputRef = useRef<HTMLInputElement>(null);

  const pageRowIds = ctx?.selectablePageRowIds ?? ctx?.pageRowIds ?? [];
  const allSelected =
    !!ctx &&
    pageRowIds.length > 0 &&
    pageRowIds.every((id) => ctx.selectedIds.has(id));
  const someSelected =
    !!ctx && pageRowIds.some((id) => ctx.selectedIds.has(id));

  useEffect(() => {
    const el = inputRef.current;
    if (!el || !ctx) {
      return;
    }
    el.indeterminate = someSelected && !allSelected;
  }, [ctx, someSelected, allSelected]);

  if (!ctx) {
    return null;
  }

  const { onTogglePage, headerLabel } = ctx;
  const label = headerLabel?.trim() || null;

  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={inputRef}
        type="checkbox"
        className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 accent-Default focus:outline-none focus:ring-2 focus:ring-Default/40"
        checked={allSelected}
        onChange={() => onTogglePage(pageRowIds, !allSelected)}
        aria-label={
          label
            ? `${label} — select all rows on this page`
            : "Seleccionar todas las filas de esta página"
        }
        title={label ? `${label} (page)` : "Seleccionar página"}
      />
      {label ? <span className="whitespace-nowrap font-normal">{label}</span> : null}
    </span>
  );
}

export function getSelectionColumnDefinition(headerLabel?: string): IHeader {
  const label = headerLabel?.trim();
  return {
    id: SYNTHETIC_COLUMN_IDS.SELECT,
    header: label || "Select",
    enableSorting: false,
    columnOrderFixed: true,
    width: label ? 120 : 48,
    headerContent: <SelectAllHeaderControl />,
    cell: (p) => <SelectionRowCell {...p} />,
  };
}

export function getIndexColumnDefinition(): IHeader {
  return {
    id: SYNTHETIC_COLUMN_IDS.INDEX,
    header: "#",
    enableSorting: false,
    columnOrderFixed: true,
    width: 44,
    cell: (p) => <IndexColumnCell {...p} />,
  };
}

export function buildSyntheticColumnHeaders(options: {
  showRowNumber: boolean;
  showSelection: boolean;
  selectionHeaderLabel?: string;
}): IHeader[] {
  const out: IHeader[] = [];
  if (options.showSelection) {
    out.push(getSelectionColumnDefinition(options.selectionHeaderLabel));
  }
  if (options.showRowNumber) {
    out.push(getIndexColumnDefinition());
  }
  return out;
}
